import { iaRa } from 'lib/helpers/utility';
import {
  PAYMENT_METHOD_FIELDS,
  isPaymentMethodModuleEnabled
} from 'lib/constants/healthcareProgram/paymentSetup';

import Disclosure from 'components/shared/Disclosure/Disclosure';
import {
  usePaymentContext,
  usePaymentContextApi
} from 'components/Payments/NewPayment/PaymentContent/context/PaymentContext';
import PaymentMethodsSkeleton from 'components/Payments/NewPayment/PaymentContent/components/PaymentMethods/PaymentMethodsSkeleton';

import { useLetter } from '../Letter/LetterContext';
import { isSelfPayPhase } from '../Letter/lib/helpers';
import {
  CreditCardChildrenLocked,
  applyPhaseTitleOverrides,
  paymentMethods
} from '../lib/constants';

const COF_CAPABLE_METHODS = new Set([
  PAYMENT_METHOD_FIELDS.PRE_AUTH,
  PAYMENT_METHOD_FIELDS.RECURRING_CHARGE,
  PAYMENT_METHOD_FIELDS.CREDIT_DEBIT_CARD
]);

const PaymentMethods = () => {
  const {
    balance,
    activePaymentMethod,
    setActivePaymentMethod,
    config,
    practiceModules,
    activePhaseField,
    cofEnforced
  } = useLetter();
  const activePhase = iaRa(config?.phases).find((p) => p.field === activePhaseField);
  const isSelfPay = isSelfPayPhase(activePhase);
  const { isTerminalLoading, processor, values, terminal, isKiosk } = usePaymentContext();
  const { setFieldValue } = usePaymentContextApi();

  const enabledFields = new Set(
    iaRa(config?.payment_methods)
      .filter((m) => {
        const channelEnabled = isKiosk ? m.in_person : m.remote;
        if (!channelEnabled) return false;
        if (
          isKiosk &&
          m.field === PAYMENT_METHOD_FIELDS.RECURRING_CHARGE &&
          !m.config?.offer_on_kiosk
        ) {
          return false;
        }
        return true;
      })
      .map((m) => m.field)
  );

  const HC_FIELD_SET = new Set(Object.values(PAYMENT_METHOD_FIELDS));

  const filteredPaymentMethods = applyPhaseTitleOverrides(
    paymentMethods
      .filter((method) => {
        if (method.hidden) return false;
        if (!isPaymentMethodModuleEnabled(practiceModules, method.id)) return false;
        if (
          HC_FIELD_SET.has(method.id) &&
          enabledFields.size > 0 &&
          !enabledFields.has(method.id)
        ) {
          return false;
        }
        if (method.kioskOnly && !isKiosk) return false;
        if (cofEnforced && !COF_CAPABLE_METHODS.has(method.id)) return false;

        const isPreAuth = method.id === PAYMENT_METHOD_FIELDS.PRE_AUTH;
        const isCreditCard = method.id === PAYMENT_METHOD_FIELDS.CREDIT_DEBIT_CARD;
        const isAch = method.id === PAYMENT_METHOD_FIELDS.ACH;
        const isOnlineTerminal = terminal?.online;

        if (!isOnlineTerminal && isPreAuth) return false;
        if (!terminal && isCreditCard) return false;
        if (balance === 0 && isCreditCard) return false;
        if (isAch && (processor !== 'payroc' || !terminal)) return false;

        return true;
      })
      .map((method) =>
        cofEnforced && method.id === PAYMENT_METHOD_FIELDS.CREDIT_DEBIT_CARD
          ? { ...method, children: CreditCardChildrenLocked }
          : method
      ),
    { isSelfPay }
  );

  if (isTerminalLoading)
    return (
      <PaymentMethodsSkeleton
        itemClassName="flex-col items-start"
        count={filteredPaymentMethods.length || 6}
      />
    );

  return (
    <>
      {filteredPaymentMethods.map((method) => {
        const isOpen = activePaymentMethod === method.id;
        const Children = method.children;
        const disableOpen =
          method.id !== PAYMENT_METHOD_FIELDS.CHECK &&
          method.id !== PAYMENT_METHOD_FIELDS.CASH &&
          !(processor !== 'payroc' && method.id === PAYMENT_METHOD_FIELDS.CREDIT_DEBIT_CARD);
        return (
          <Disclosure
            key={method.id}
            title={method.title}
            icon={method.icon}
            caption={method.caption}
            onToggle={() => setActivePaymentMethod(method.id)}
            bodyClassName="flex-col items-start"
            isOpen={isOpen}
            disableOpen={disableOpen}>
            {Children && <Children values={values} setFieldValue={setFieldValue} />}
          </Disclosure>
        );
      })}
    </>
  );
};

export default PaymentMethods;
